// ORGAO OU ENTIDADE — o vocabulario da classificacao institucional.
//
// A Atlas atende prefeituras, mas nao so' elas: assembleias legislativas,
// camaras, autarquias, institutos de previdencia, fundacoes, empresas publicas.
// Ate 04/09/2026 todas entravam no sistema CHAMADAS de prefeitura, e a unica
// classificacao disponivel misturava tipo com natureza administrativa — o valor
// "Administracao direta (a prefeitura)" fazia de administracao direta um sinonimo
// de prefeitura, e nao havia como classificar uma secretaria estadual.
//
// QUATRO EIXOS INDEPENDENTES, e e' esse o ponto:
//
//   tipo               o que a organizacao E'          (prefeitura, autarquia...)
//   esfera             em que nivel da federacao       (municipal, estadual...)
//   poder              a que poder pertence            (executivo, legislativo...)
//   classificacaoAdm   direta, indireta, nao se aplica
//
// Uma assembleia legislativa e' assembleia + estadual + legislativo + direta; uma
// autarquia municipal e' autarquia + municipal + executivo + indireta; uma
// secretaria estadual e' secretaria + estadual + executivo + direta. Nenhum dos
// quatro deriva do outro por regra: o tipo apenas SUGERE os demais no cadastro, e
// o operador corrige.
//
// ESTE ARQUIVO E' DADO, NAO REGRA. Nenhuma regra de negocio pode ramificar em
// tipo, esfera, poder ou classificacao — margem, rubrica, convenio, folha, trava
// e layout enxergam a unidade pagadora pelo id, nunca pelo que ela e'. O portao
// que impede isso e' `classificacao-nao-ramifica.test.ts`, em @atlas/domain.
//
// Vive em @atlas/types (a folha da arvore) porque API e web precisam da MESMA
// lista: a API valida contra ela, a tela monta o seletor com ela.
// ---------------------------------------------------------------- tipos
/**
 * O que a organizacao e'.
 *
 * MUNICIPIO NAO E' UM TIPO, de proposito. O municipio nao loga, nao assina
 * convenio, nao processa folha e nao recebe arquivo — quem faz isso e' a
 * Prefeitura (o Poder Executivo municipal), a Camara, a autarquia. Ele continua
 * sendo o que sempre foi: o codigo IBGE e o nome, usados para agrupar e exibir.
 */
export const TIPOS_ORGANIZACAO = [
    "prefeitura",
    "camara_municipal",
    "governo_estadual",
    "assembleia_legislativa",
    "tribunal",
    "ministerio_publico",
    "tribunal_de_contas",
    "defensoria_publica",
    "secretaria",
    "autarquia",
    "fundacao",
    "fundo",
    "instituto_previdencia",
    "empresa_publica",
    "consorcio_publico",
    "outra",
];
export const ESFERAS = ["municipal", "estadual", "federal"];
export const PODERES = ["executivo", "legislativo", "judiciario", "autonomo", "outro"];
/** Natureza administrativa, independente do tipo. `nao_se_aplica` cobre consorcio
 *  publico e o que nao se encaixa — e' um valor legitimo, nao um "nao sei". */
export const CLASSIFICACOES_ADM = ["direta", "indireta", "nao_se_aplica"];
/**
 * O que uma organizacao exerce POR outra, numa relacao institucional.
 *
 * Sao QUATRO, e nao os oito que o pedido de 04/09/2026 listou: papel sem efeito
 * no produto e' campo que ninguem mantem atualizado. `TITULAR` e
 * `ORGAO_EMPREGADOR` sairam porque descrevem a propria organizacao (sem
 * delegacao, ela ja exerce todos os papeis sobre si); `UNIDADE_PAGADORA` saiu
 * porque "quem paga o salario" nao altera nada aqui e o nome colide com o termo
 * do glossario, que significa a folha; `GESTOR_CONVENIOS` foi absorvido por
 * `administra_consignacoes`.
 */
export const PAPEIS = [
    /** Troca de arquivos, competencias e relatorios de folha da atendida. */
    "processa_folha",
    /** Servidores, contratos, ADF, anuencia e configuracao da atendida. */
    "administra_consignacoes",
    /** So' leitura: servidores, contratos, ADF, margem, relatorios. */
    "presta_atendimento",
    /** Informativo, mais o relatorio de contratos por banco. */
    "responsavel_repasse",
];
// ---------------------------------------------------------------- rotulos
/** O que a interface mostra. Fonte unica: telas, documentos e API leem daqui. */
export const ROTULOS = {
    tipo: {
        prefeitura: "Prefeitura",
        camara_municipal: "Câmara municipal",
        governo_estadual: "Governo estadual",
        assembleia_legislativa: "Assembleia legislativa",
        tribunal: "Tribunal",
        ministerio_publico: "Ministério Público",
        tribunal_de_contas: "Tribunal de Contas",
        defensoria_publica: "Defensoria Pública",
        secretaria: "Secretaria",
        autarquia: "Autarquia",
        fundacao: "Fundação pública",
        fundo: "Fundo",
        instituto_previdencia: "Instituto de previdência",
        empresa_publica: "Empresa pública",
        consorcio_publico: "Consórcio público",
        outra: "Outro órgão ou entidade",
    },
    esfera: {
        municipal: "Municipal",
        estadual: "Estadual",
        federal: "Federal",
    },
    poder: {
        executivo: "Executivo",
        legislativo: "Legislativo",
        judiciario: "Judiciário",
        autonomo: "Autônomo",
        outro: "Outro",
    },
    classificacaoAdm: {
        direta: "Administração direta",
        indireta: "Administração indireta",
        nao_se_aplica: "Não se aplica",
    },
    papel: {
        processa_folha: "Processa a folha",
        administra_consignacoes: "Administra as consignações",
        presta_atendimento: "Presta atendimento",
        responsavel_repasse: "Responsável pelo repasse",
    },
};
/** Uma frase por papel, para a tela explicar o que a escolha libera. */
export const DESCRICAO_PAPEL = {
    processa_folha: "Envia e recebe os arquivos da folha da organização atendida, abre e fecha competências.",
    administra_consignacoes: "Cadastra e edita servidores, contratos, ADF e anuência da organização atendida.",
    presta_atendimento: "Consulta servidores, contratos, margem e relatórios da organização atendida, sem alterar nada.",
    responsavel_repasse: "Responde pelo repasse aos credores; vê contratos e o relatório por banco.",
};
// ---------------------------------------------------------------- situacao
/** Estado de uma relacao numa competencia. Derivado, nunca gravado. */
export const SITUACOES_RELACAO = ["agendada", "vigente", "suspensa", "encerrada"];
export const ROTULO_SITUACAO_RELACAO = {
    agendada: "Agendada",
    vigente: "Vigente",
    suspensa: "Suspensa",
    encerrada: "Encerrada",
};
/**
 * O que cada papel libera, por recurso do portal.
 *
 * `"escrita"` implica leitura. Recurso ausente = o papel nao alcanca aquela
 * tela.
 *
 * MORA AQUI, E NAO NO DOMINIO, pela mesma razao de `PREFEITURA_RESOURCE_GROUPS`
 * e de `permissaoCobre`: os DOIS lados precisam decidir igual. O servidor usa a
 * tabela para recusar; o menu do portal usa a mesma tabela para desabilitar o
 * item que a unidade ativa nao alcanca. Enquanto ela estava so' no dominio
 * (API-only), a tela teria de manter uma copia — e uma copia divergente aqui
 * significa um item de menu que abre e responde 403, ou um que some sem motivo.
 * `@atlas/domain` a reexporta, entao nada que a importava de la' muda.
 *
 * `perfis` e `conta` NAO aparecem em papel nenhum, de proposito: sao sempre da
 * organizacao de ORIGEM. Ninguem administra os usuarios de outra organizacao
 * por delegacao de folha — quem entra na conta de alguem e' quem tem a
 * credencial dela, e delegar atendimento nao pode virar delegacao de
 * identidade.
 */
export const PAPEL_LIBERA = {
    processa_folha: {
        dashboard: "leitura",
        "troca-arquivos": "escrita",
        folhas: "escrita",
        servidores: "leitura",
        contratos: "leitura",
        adf: "leitura",
        relatorios: "leitura",
        comunicados: "leitura",
        // #279: quem envia a folha registra o que repassou dela.
        repasses: "escrita",
    },
    administra_consignacoes: {
        dashboard: "leitura",
        "troca-arquivos": "leitura",
        folhas: "leitura",
        servidores: "escrita",
        contratos: "escrita",
        adf: "escrita",
        anuencia: "escrita",
        config: "escrita",
        convenios: "leitura",
        relatorios: "leitura",
        comunicados: "leitura",
        repasses: "leitura",
    },
    presta_atendimento: {
        dashboard: "leitura",
        servidores: "leitura",
        contratos: "leitura",
        adf: "leitura",
        relatorios: "leitura",
        comunicados: "leitura",
        repasses: "leitura",
    },
    responsavel_repasse: {
        dashboard: "leitura",
        contratos: "leitura",
        convenios: "leitura",
        relatorios: "leitura",
        // #279: o papel que responde pelo repasse e' o que o registra.
        repasses: "escrita",
    },
};
/** Algum dos papeis libera este recurso neste modo? */
export function papelLibera(papeis, recurso, modo) {
    for (const papel of papeis) {
        const concedido = PAPEL_LIBERA[papel]?.[recurso];
        if (!concedido)
            continue;
        if (concedido === "escrita")
            return true;
        if (modo === "leitura")
            return true;
    }
    return false;
}
//# sourceMappingURL=organizacao.js.map